import React from 'react';
import { Category, SavedQuery } from '@/types';
import { foldPredicates } from '@/util/fp';
import { escapeRegexString } from '@/util/escapeRegexString';

type FilterType = 'ALL' | 'MY_QUERIES' | 'MY_STARRED';

export type CategoryFilter = 'ALL' | Category;

type FilterTerm = string;

const FILTER_TYPE_TO_PREDICATE_MAP: Record<
  FilterType,
  Predicate<SavedQuery>
> = {
  ALL: () => true,
  MY_QUERIES: (q) => q.isOwner,
  MY_STARRED: (q) => q.isFavourite,
};

export const useSavedQueryFilter = (): [
  FilterTerm,
  FilterType,
  CategoryFilter,
  F<FilterTerm>,
  F<FilterType>,
  F<CategoryFilter>,
  Predicate<SavedQuery>,
] => {
  const [filterTerm, setFilterTerm] = React.useState<FilterTerm>('');
  const [filterType, setFilterType] = React.useState<FilterType>('ALL');
  const [category, setCategory] = React.useState<CategoryFilter>('ALL');

  const filterTermRegExp = new RegExp(escapeRegexString(filterTerm), 'i');

  const emptyTermPredicate = () => filterTerm === '';
  const queryNamePredicate = (q: SavedQuery) => filterTermRegExp.test(q.name);
  const queryOwnerPredicate = (q: SavedQuery) => filterTermRegExp.test(q.email);

  const searchTermP = foldPredicates.withOr(
    emptyTermPredicate,
    queryNamePredicate,
    queryOwnerPredicate,
  );

  const filterByCategory = (q: SavedQuery) =>
    category === 'ALL' ? true : q.categoryIds.includes(category.id);

  const savedQueryPredicate = foldPredicates.withAnd(
    filterByCategory,
    FILTER_TYPE_TO_PREDICATE_MAP[filterType],
    searchTermP,
  );

  return [
    filterTerm,
    filterType,
    category,
    setFilterTerm,
    setFilterType,
    setCategory,
    savedQueryPredicate,
  ];
};
